import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, DataCell, DataList, Dialog, Flex, Text } from '@/components/ui';
import { EventTrailEntry } from '@/types/eventTrails';
import { formatFileSize } from '@/utils/fileUtils';
import { IconExternalLink } from '@tabler/icons-react';

interface EventDetailsModalProps {
  event: EventTrailEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
  event,
  open,
  onOpenChange,
}) => {
  const items = useMemo(() => {
    if (!event) return [];

    const result: Array<{ label: string; value: React.ReactNode }> = [
      {
        label: 'Event ID',
        value: <DataCell noWrapper type="id" value={event.id} />,
      },
      {
        label: 'Action',
        value: <DataCell noWrapper type="action" value={event.action} />,
      },
      {
        label: 'Event Type',
        value: <DataCell noWrapper type="text" value={event.event_type} />,
      },
      {
        label: 'Entity',
        value: (
          <Flex align="center" gap="2">
            <DataCell
              noWrapper
              type="text"
              value={`${event.entity_type}: ${event.entity_id}`}
              render={() => (
                <Text style={{ fontFamily: 'var(--font-mono)' }}>
                  {event.entity_type}: {event.entity_id}
                </Text>
              )}
            />
            {event.entity_type === 'import_mapping' &&
              event.event_type === 'create' && (
                <Button
                  as={Link}
                  variant="outlined"
                  color="default"
                  size="xsmall"
                  to={`/data-import/mapping/${event.entity_id}`}
                  startIcon={<IconExternalLink />}
                >
                  View Import
                </Button>
              )}
          </Flex>
        ),
      },
      {
        label: 'User',
        value: (
          <DataCell
            noWrapper
            type="user"
            user={event.user}
            showEmail={true}
          />
        ),
      },
      {
        label: 'Date & Time',
        value: (
          <DataCell
            noWrapper
            type="datetime"
            value={event.created_at}
            relative={false}
            format="YYYY-MM-DD HH:mm:ss"
          />
        ),
      },
    ];

    // Add file information if available
    if (event.data.filename) {
      result.push({
        label: 'File Information',
        value: (
          <Flex direction="column" gap="0">
            <DataCell
              noWrapper
              type="fileName"
              value={event.data.filename}
              filename={event.data.filename}
            />
            {event.data.size && (
              <Text size="3xsmall" color="gray">
                Size: {formatFileSize(event.data.size)}
              </Text>
            )}
            {event.data.content_type && (
              <Text size="3xsmall" color="gray">
                Type: {event.data.content_type}
              </Text>
            )}
            {event.data.checksum && (
              <Text
                size="3xsmall"
                color="gray"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                Checksum: {event.data.checksum}
              </Text>
            )}
          </Flex>
        ),
      });
    }

    // Add IP address if available
    if (event.ip_address) {
      result.push({
        label: 'IP Address',
        value: <DataCell noWrapper type="text" value={event.ip_address} />,
      });
    }

    // Add user agent if available
    if (event.user_agent) {
      result.push({
        label: 'User Agent',
        value: (
          <DataCell
            noWrapper
            type="text"
            value={event.user_agent}
            render={() => (
              <Text size="3xsmall" style={{ fontFamily: 'var(--font-mono)' }}>
                {event.user_agent}
              </Text>
            )}
          />
        ),
      });
    }

    return result;
  }, [event]);

  if (!event) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="audit-trail-details-modal" size="large">
        <Dialog.Header
          title="Event Details"
          subtitle={`Detailed information about the ${event.action.toLowerCase()} event`}
        />

        <Dialog.Body>
          <DataList items={items} orientation="horizontal" size="large" />
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Root>
  );
};
